import {
  DataPopper,
  DataPopperRow,
  Icons,
  Tooltip,
  Typography,
  Box,
  Stack,
} from '@cast/design-system';
import { dashesOrNum } from '@cast/utils';

import { Money } from 'components/money';
import { DataTooltip } from '@cast/components-data-tooltip';

import { Savings } from '../../_types';
import { RatioProgressBarWithLoading } from '../../workloads-list/SummarySection/_components/RatioProgressBarWithLoading';

type Props = {
  savings: Savings;
  isLoading?: boolean;
};

export const SavingsSummaryProgress = ({
  savings: {
    estimatedMonthlySavings,
    estimatedMonthlySavingsPercentage,
    currentMonthlyCost,
    currentRecommendedMonthlyCost,
  },
  isLoading,
}: Props) => {
  return (
    <Stack gap={8} data-testid="savings-summary">
      <Stack direction="row" gap={4}>
        <Typography variant="L10R">Estimated monthly savings</Typography>
        <Tooltip
          title="Shows how monthly costs would change if resource recommendations are applied."
          placement="top"
        >
          <Icons.Info size={12} />
        </Tooltip>
      </Stack>
      <Stack rowGap={4}>
        <Typography variant="h3" data-testid="estimated-savings">
          <Money amount={estimatedMonthlySavings} dashesIfUndefined />
        </Typography>
        <Box>
          <DataTooltip
            wrapIf={!isLoading}
            placement="bottom"
            title={
              <DataPopper sx={{ minWidth: 242 }} size="small">
                <DataPopperRow
                  indicator={{ color: 'green.600', size: 'small' }}
                  left={
                    <Typography variant="P10M">Reduction in cost</Typography>
                  }
                  right={
                    <Typography variant="P10B">
                      {dashesOrNum({
                        value: estimatedMonthlySavingsPercentage,
                        roundBy: 2,
                      })}
                      %
                    </Typography>
                  }
                  dividerBottom
                />
                <DataPopperRow
                  left={
                    <Typography variant="P10R" pl={12}>
                      Current cost
                    </Typography>
                  }
                  right={
                    <Typography variant="P10B">
                      <Money amount={currentMonthlyCost} dashesIfUndefined />
                    </Typography>
                  }
                />
                <DataPopperRow
                  left={
                    <Typography variant="P10R" pl={12}>
                      Estimated monthly savings
                    </Typography>
                  }
                  right={
                    <Typography variant="P10B">
                      <Money
                        amount={estimatedMonthlySavings}
                        dashesIfUndefined
                      />
                    </Typography>
                  }
                />
              </DataPopper>
            }
          >
            <div>
              <RatioProgressBarWithLoading
                testId="savings-progress-bar"
                hideBorders
                size="lg"
                total={
                  (currentRecommendedMonthlyCost || 0) +
                  (estimatedMonthlySavings || 0)
                }
                bars={[
                  { value: estimatedMonthlySavings || 0, color: 'green.600' },
                  {
                    value: currentRecommendedMonthlyCost || 0,
                    color: 'green.100',
                  },
                ]}
                isLoading={!!isLoading}
              />
            </div>
          </DataTooltip>
        </Box>

        <Stack direction="row" alignItems="baseline">
          <Typography
            variant="h6"
            color="green.600"
            data-testid="savings-percentage"
          >
            {dashesOrNum({
              value: estimatedMonthlySavingsPercentage,
              roundBy: 2,
            })}
            %
          </Typography>
          <Typography variant="L10R" color="textSecondary" pl={4}>
            reduction in cost
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
};
